import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  useToast,
} from '@chakra-ui/react';
import api from '../../../../utils/api';
import analytics from '../../../../segmentTracking';

const CourseModal = ({ isOpen, onClose, course, onCourseSaved }) => {
  const [formData, setFormData] = useState({
    name: '',
  });
  const [loading, setLoading] = useState(false);
  const [currentUser] = useState(JSON.parse(localStorage.getItem('user')));
  const [currentInstitution] = useState(
    JSON.parse(localStorage.getItem('institution')),
  );

  const toast = useToast();
  const isEditing = !!course;

  // No need to fetch schools since API doesn't use them

  // Initialize form when course changes or modal opens
  useEffect(() => {
    if (isOpen) {
      if (course) {
        setFormData({
          name: course.name || '',
        });
      } else {
        setFormData({
          name: '',
        });
      }
    }
  }, [course, isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Course name is required',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);

    try {
      const payload = {
        name: formData.name.trim(),
        institution: currentInstitution._id,
      };

      let response;
      if (isEditing) {
        response = await api.patch(`/dashboard/courses/${course._id}`, payload);

        analytics.track('settings:academic:courses:updated', {
          courseId: course._id,
          courseName: payload.name,
          userRole: currentUser?.role,
          email: currentUser?.email,
        });

        toast({
          title: 'Success',
          description: 'Course updated successfully',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } else {
        response = await api.post('/dashboard/courses', payload);

        analytics.track('settings:academic:courses:created', {
          courseId: response.data.course?._id,
          courseName: payload.name,
          userRole: currentUser?.role,
          email: currentUser?.email,
        });

        toast({
          title: 'Success',
          description: 'Course created successfully',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      }

      onCourseSaved(response.data.course);
      handleClose();
    } catch (error) {
      console.error('Error saving course:', error);
      toast({
        title: 'Error',
        description:
          error?.response?.data?.message ||
          `Failed to ${isEditing ? 'update' : 'create'} course`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      // Reset form fields only if this is an "add" modal
      // For edit modals, let the useEffect handle repopulation on next open
      if (!course) {
        setFormData({
          name: '',
        });
      }
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {isEditing ? 'Edit Course' : 'Add New Course'}
        </ModalHeader>
        <ModalCloseButton isDisabled={loading} />

        <form onSubmit={handleSubmit}>
          <ModalBody pb={6}>
            <FormControl isRequired mb={4}>
              <FormLabel>Course Name</FormLabel>
              <Input
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter course name"
                isDisabled={loading}
              />
            </FormControl>
          </ModalBody>

          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={handleClose}
              isDisabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              bg="#EF6506"
              color="white"
              _hover={{ bg: '#D55A05' }}
              isLoading={loading}
              loadingText={isEditing ? 'Updating...' : 'Creating...'}
            >
              {isEditing ? 'Update Course' : 'Create Course'}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default CourseModal;
